import {
  isPackDescriptor,
  packFileName,
  packTotalBytes,
  searchFileName,
  saveLocalPacks,
  type PackDescriptor,
} from "../storage/manifest";
import {
  deletePackFile,
  openPackForWrite,
  readPackFile,
  storageBudget,
} from "../storage/opfs";
import { importPackSearch } from "../storage/search";
import {
  decrypt,
  encrypt,
  importEncryptionKey,
  messageArrayBuffer,
  messageOffset,
} from "./crypto";
import { PeerLink, type PeerStatus } from "./peerLink";

// Peermonger uses the same size: encrypted messages remain at or below 64 KiB
// after the 16-byte IV, 8-byte offset and 16-byte GCM authentication tag.
const CHUNK_BYTES = 65_496;
const MAX_BUFFERED_BYTES = 4 * 1024 * 1024;
const BUFFER_LOW_BYTES = 1024 * 1024;
const CHECKPOINT_BYTES = 32 * 1024 * 1024;

interface TransferCallbacks {
  onStatus: (status: PeerStatus | "transferring" | "complete") => void;
  onProgress: (pack: PackDescriptor, received: number, total: number) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
}

type ManifestMessage = {
  kind: "manifest";
  protocol: 2;
  packs: PackDescriptor[];
};

type ReadyMessage = { kind: "ready"; resourceId: string; offset: number };
type CompleteMessage = { kind: "complete" };
type ErrorMessage = { kind: "error"; message: string };
type ControlMessage = ReadyMessage | CompleteMessage | ErrorMessage;

function decodeJson(data: ArrayBuffer): unknown {
  return JSON.parse(new TextDecoder().decode(data)) as unknown;
}

function isControlMessage(value: unknown): value is ControlMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<ControlMessage> & {
    resourceId?: unknown;
    offset?: unknown;
  };
  if (message.kind === "complete") return true;
  if (message.kind === "error") return typeof message.message === "string";
  return (
    message.kind === "ready" &&
    typeof message.resourceId === "string" &&
    typeof message.offset === "number" &&
    Number.isSafeInteger(message.offset) &&
    message.offset >= 0
  );
}

interface TransferResource {
  id: string;
  pack: PackDescriptor;
  kind: "map" | "search";
  bytes: number;
  filename: string;
}

function transferResources(packs: PackDescriptor[]): TransferResource[] {
  return packs.flatMap((pack) => {
    const resources: TransferResource[] = [
      {
        id: `${pack.id}:map`,
        pack,
        kind: "map",
        bytes: pack.bytes,
        filename: packFileName(pack),
      },
    ];
    if (pack.search) {
      resources.push({
        id: `${pack.id}:search`,
        pack,
        kind: "search",
        bytes: pack.search.bytes,
        filename: searchFileName(pack),
      });
    }
    return resources;
  });
}

function resourceProgress(
  resources: TransferResource[],
  active: TransferResource,
  received: number,
): number {
  let complete = 0;
  for (const resource of resources) {
    if (resource === active) break;
    if (resource.pack.id === active.pack.id) complete += resource.bytes;
  }
  return complete + received;
}

async function sendJson(channel: RTCDataChannel, key: CryptoKey, value: unknown): Promise<void> {
  const plain = new TextEncoder().encode(JSON.stringify(value));
  channel.send(await encrypt(key, plain));
}

export class MapShareSender {
  private link: PeerLink | null = null;
  private channel: RTCDataChannel | null = null;
  private encryptionKey: CryptoKey | null = null;
  private sending = false;
  private stopped = false;
  private offeredPacks: PackDescriptor[] = [];
  private resources: TransferResource[] = [];

  constructor(
    private readonly rawKey: Uint8Array<ArrayBuffer>,
    private readonly packs: PackDescriptor[],
    private readonly callbacks: TransferCallbacks,
  ) {}

  async start(): Promise<void> {
    this.encryptionKey = await importEncryptionKey(this.rawKey);
    this.link = new PeerLink({
      key: this.rawKey,
      sender: true,
      onStatus: this.callbacks.onStatus,
      onError: (error) => this.fail(error),
      onChannel: (channel) => void this.channelReady(channel),
    });
    await this.link.start();
  }

  stop(): void {
    this.stopped = true;
    this.channel?.close();
    this.link?.close();
  }

  private async channelReady(channel: RTCDataChannel): Promise<void> {
    if (!this.encryptionKey || this.stopped) return;
    this.channel = channel;
    channel.bufferedAmountLowThreshold = BUFFER_LOW_BYTES;
    channel.addEventListener("message", (event) => void this.handleControl(event.data));
    try {
      // Search is an enhancement, not a reason to make an otherwise complete
      // chart unshareable. Old installs may predate the raw sidecar; omit its
      // descriptor until a map server has supplied it again.
      this.offeredPacks = [];
      for (const pack of this.packs) {
        if (!pack.search) {
          this.offeredPacks.push(pack);
          continue;
        }
        const search = await readPackFile(searchFileName(pack));
        this.offeredPacks.push(
          search?.size === pack.search.bytes ? pack : { ...pack, search: undefined },
        );
      }
      this.resources = transferResources(this.offeredPacks);
      const manifest: ManifestMessage = {
        kind: "manifest",
        protocol: 2,
        packs: this.offeredPacks,
      };
      await sendJson(channel, this.encryptionKey, manifest);
    } catch (error) {
      this.fail(error instanceof Error ? error : new Error("Could not offer the map."));
    }
  }

  private async handleControl(raw: unknown): Promise<void> {
    try {
      if (!this.encryptionKey) return;
      const message = decodeJson(
        await decrypt(this.encryptionKey, await messageArrayBuffer(raw)),
      );
      if (!isControlMessage(message)) throw new Error("The receiver sent an invalid response.");
      if (message.kind === "error") throw new Error(message.message);
      if (message.kind === "complete") {
        this.callbacks.onStatus("complete");
        this.callbacks.onComplete();
        return;
      }
      if (this.sending) throw new Error("The receiver requested two files at once.");
      const resource = this.resources.find((candidate) => candidate.id === message.resourceId);
      if (!resource || message.offset > resource.bytes) {
        throw new Error("The receiver requested an invalid map offset.");
      }
      void this.sendResource(resource, message.offset);
    } catch (error) {
      this.fail(error instanceof Error ? error : new Error("The receiver response failed."));
    }
  }

  private async sendResource(resource: TransferResource, offset: number): Promise<void> {
    if (!this.channel || !this.encryptionKey) return;
    this.sending = true;
    this.callbacks.onStatus("transferring");
    try {
      const file = await readPackFile(resource.filename);
      if (!file || file.size !== resource.bytes) {
        throw new Error(`${resource.pack.label} is no longer completely stored on this device.`);
      }
      for (let position = offset; position < file.size; position += CHUNK_BYTES) {
        if (this.stopped || this.channel.readyState !== "open") {
          throw new Error("The peer connection closed before the map was sent.");
        }
        await this.waitForBuffer();
        const end = Math.min(position + CHUNK_BYTES, file.size);
        const plain = await file.slice(position, end).arrayBuffer();
        this.channel.send(await encrypt(this.encryptionKey, plain, position));
        this.callbacks.onProgress(
          resource.pack,
          resourceProgress(this.resources, resource, end),
          packTotalBytes(resource.pack),
        );
      }
    } catch (error) {
      const failure = error instanceof Error ? error : new Error("Map transfer failed.");
      await this.sendError(failure.message);
      this.fail(failure);
    } finally {
      this.sending = false;
    }
  }

  private async waitForBuffer(): Promise<void> {
    const channel = this.channel!;
    while (channel.bufferedAmount > MAX_BUFFERED_BYTES && channel.readyState === "open") {
      await new Promise<void>((resolve, reject) => {
        const opened = () => {
          cleanup();
          resolve();
        };
        const closed = () => {
          cleanup();
          reject(new Error("The peer connection closed during transfer."));
        };
        const cleanup = () => {
          channel.removeEventListener("bufferedamountlow", opened);
          channel.removeEventListener("close", closed);
        };
        channel.addEventListener("bufferedamountlow", opened, { once: true });
        channel.addEventListener("close", closed, { once: true });
      });
    }
  }

  private async sendError(message: string): Promise<void> {
    if (this.channel?.readyState === "open" && this.encryptionKey) {
      await sendJson(this.channel, this.encryptionKey, { kind: "error", message } satisfies ErrorMessage)
        .catch(() => undefined);
    }
  }

  private fail(error: Error): void {
    if (!this.stopped) this.callbacks.onError(error);
  }
}

export class MapShareReceiver {
  private link: PeerLink | null = null;
  private channel: RTCDataChannel | null = null;
  private encryptionKey: CryptoKey | null = null;
  private packs: PackDescriptor[] = [];
  private resources: TransferResource[] = [];
  private resourceIndex = -1;
  private activeResource: TransferResource | null = null;
  private writable: FileSystemWritableFileStream | null = null;
  private received = 0;
  private sinceCheckpoint = 0;
  private receiveQueue: Promise<void> = Promise.resolve();
  private stopped = false;
  private finished = false;
  private failed = false;

  constructor(
    private readonly rawKey: Uint8Array<ArrayBuffer>,
    private readonly callbacks: TransferCallbacks,
    private readonly onManifest: (packs: PackDescriptor[]) => void,
  ) {}

  async start(): Promise<void> {
    this.encryptionKey = await importEncryptionKey(this.rawKey);
    this.link = new PeerLink({
      key: this.rawKey,
      sender: false,
      onStatus: this.callbacks.onStatus,
      onError: (error) => this.fail(error),
      onChannel: (channel) => this.channelReady(channel),
    });
    await this.link.start();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    await this.commitPartial();
    this.channel?.close();
    this.link?.close();
  }

  private channelReady(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.addEventListener("message", (event) => {
      this.receiveQueue = this.receiveQueue
        .then(() => this.handleMessage(event.data))
        .catch((error: unknown) => {
          void this.reportFailure(
            error instanceof Error ? error : new Error("Could not receive the map."),
          );
        });
    });
    channel.addEventListener("close", () => {
      if (!this.finished && !this.stopped) {
        void this.commitPartial().then(() =>
          this.fail(new Error("The sender disconnected. Your partial transfer was saved.")),
        );
      }
    });
  }

  private async handleMessage(raw: unknown): Promise<void> {
    if (!this.encryptionKey || this.stopped || this.finished || this.failed) return;
    const encrypted = await messageArrayBuffer(raw);
    if (!this.packs.length) {
      const value = decodeJson(await decrypt(this.encryptionKey, encrypted));
      await this.acceptManifest(value);
      return;
    }
    if (!this.activeResource || !this.writable) {
      throw new Error("The sender transmitted map bytes before the receiver was ready.");
    }
    await this.receiveChunk(encrypted);
  }

  private async acceptManifest(value: unknown): Promise<void> {
    if (!value || typeof value !== "object") throw new Error("Invalid shared map metadata.");
    const message = value as Partial<ManifestMessage>;
    if (
      message.kind !== "manifest" ||
      message.protocol !== 2 ||
      !Array.isArray(message.packs) ||
      message.packs.length < 1 ||
      message.packs.length > 32 ||
      !message.packs.every(isPackDescriptor)
    ) {
      throw new Error("This sharing link uses unsupported or invalid map metadata.");
    }
    if (new Set(message.packs.map((pack) => pack.id)).size !== message.packs.length) {
      throw new Error("The shared map contains duplicate pack identifiers.");
    }
    this.packs = message.packs.map((pack) => ({ ...pack }));
    this.resources = transferResources(this.packs);
    this.onManifest(this.packs);

    let needed = 0;
    for (const resource of this.resources) {
      const existing = await readPackFile(resource.filename);
      if (existing?.size === resource.bytes) continue;
      needed += resource.bytes - Math.min(existing?.size ?? 0, resource.bytes);
    }
    const budget = await storageBudget();
    if (needed > budget.available) {
      throw new Error("There is not enough free storage on this device for the shared map.");
    }
    await this.advanceToNextResource();
  }

  private async advanceToNextResource(): Promise<void> {
    if (!this.channel || !this.encryptionKey) return;
    for (++this.resourceIndex; this.resourceIndex < this.resources.length; ++this.resourceIndex) {
      const resource = this.resources[this.resourceIndex];
      const existing = await readPackFile(resource.filename);
      if (existing?.size === resource.bytes) {
        this.callbacks.onProgress(
          resource.pack,
          resourceProgress(this.resources, resource, resource.bytes),
          packTotalBytes(resource.pack),
        );
        continue;
      }
      let offset = existing?.size ?? 0;
      if (offset > resource.bytes) {
        await deletePackFile(resource.filename);
        offset = 0;
      }
      this.activeResource = resource;
      this.received = offset;
      this.sinceCheckpoint = 0;
      this.writable = await openPackForWrite(resource.filename, {
        keepExisting: offset > 0,
      });
      if (offset > 0) await this.writable.seek(offset);
      this.callbacks.onProgress(
        resource.pack,
        resourceProgress(this.resources, resource, offset),
        packTotalBytes(resource.pack),
      );
      await sendJson(this.channel, this.encryptionKey, {
        kind: "ready",
        resourceId: resource.id,
        offset,
      } satisfies ReadyMessage);
      return;
    }
    await this.finish();
  }

  private async receiveChunk(encrypted: ArrayBuffer): Promise<void> {
    const resource = this.activeResource!;
    const offset = messageOffset(encrypted);
    if (offset !== this.received) {
      throw new Error(
        `The map arrived out of order (expected ${this.received}, received ${offset}).`,
      );
    }
    const plain = await decrypt(this.encryptionKey!, encrypted);
    if (this.received + plain.byteLength > resource.bytes) {
      throw new Error("The sender transmitted more map data than advertised.");
    }
    await this.writable!.write(new Uint8Array(plain));
    this.received += plain.byteLength;
    this.sinceCheckpoint += plain.byteLength;
    this.callbacks.onStatus("transferring");
    this.callbacks.onProgress(
      resource.pack,
      resourceProgress(this.resources, resource, this.received),
      packTotalBytes(resource.pack),
    );

    if (this.received === resource.bytes) {
      await this.writable!.close();
      this.writable = null;
      const stored = await readPackFile(resource.filename);
      if (stored?.size !== resource.bytes) {
        throw new Error(`${resource.pack.label} was not committed completely to device storage.`);
      }
      if (resource.kind === "search") await importPackSearch(resource.pack);
      this.activeResource = null;
      await this.advanceToNextResource();
    } else if (this.sinceCheckpoint >= CHECKPOINT_BYTES) {
      await this.writable!.close();
      this.writable = await openPackForWrite(resource.filename, { keepExisting: true });
      await this.writable.seek(this.received);
      this.sinceCheckpoint = 0;
    }
  }

  private async finish(): Promise<void> {
    if (!this.channel || !this.encryptionKey) return;
    for (const pack of this.packs) await importPackSearch(pack);
    saveLocalPacks(this.packs);
    this.finished = true;
    this.callbacks.onStatus("complete");
    await sendJson(this.channel, this.encryptionKey, { kind: "complete" } satisfies CompleteMessage);
    this.callbacks.onComplete();
  }

  private async commitPartial(): Promise<void> {
    if (!this.writable) return;
    const writable = this.writable;
    this.writable = null;
    await writable.close().catch(() => writable.abort().catch(() => undefined));
  }

  private async reportFailure(error: Error): Promise<void> {
    if (this.failed) return;
    this.failed = true;
    await this.commitPartial();
    if (this.channel?.readyState === "open" && this.encryptionKey) {
      await sendJson(this.channel, this.encryptionKey, {
        kind: "error",
        message: error.message,
      } satisfies ErrorMessage).catch(() => undefined);
    }
    this.fail(error);
    window.setTimeout(() => this.link?.close(), 250);
  }

  private fail(error: Error): void {
    if (!this.stopped && !this.finished) this.callbacks.onError(error);
  }
}
